import React, { useEffect, useRef } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { renderCoverDoc } from '../render/renderCoverDoc';
import { useCoverPicture } from './useCoverPicture';
import { useKitFontsReady } from './useKitFontsReady';

interface CoverThumbProps {
  doc: CoverDoc;
  /** The side of the picture in CSS pixels. */
  size: number;
  className?: string;
  watermark?: boolean;
  /** The text of the picture for a screen reader; empty for a decoration. */
  label?: string;
}

/**
 * A small drawing of a document with the one renderer (`renderCoverDoc`): a version of the strip,
 * an effect tile, a layout, the "Preview small" pill. It draws again when the document changes.
 */
const CoverThumbComponent: React.FC<CoverThumbProps> = ({ doc, size, className, watermark = false, label = '' }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const { picture } = useCoverPicture(doc.background);
  const isFontsReady = useKitFontsReady();

  useEffect(() => {
    const element = canvas.current;
    const ctx = element?.getContext('2d') ?? null;
    if (element === null || ctx === null) {
      return;
    }
    const ratio = Math.min(2, window.devicePixelRatio > 0 ? window.devicePixelRatio : 1);
    const side = Math.round(size * ratio);
    element.width = side;
    element.height = side;
    renderCoverDoc(ctx, doc, side, { picture }, { watermark });
  }, [doc, isFontsReady, picture, size, watermark]);

  return (
    <canvas
      ref={canvas}
      className={className}
      style={{ width: size, height: size }}
      role={label === '' ? 'presentation' : 'img'}
      aria-label={label === '' ? undefined : label}
    />
  );
};

export const CoverThumb = TypedMemo(CoverThumbComponent);
